import React from 'react';
import { Link } from 'react-router-dom';
import UnderReview from './components/UnderReview';
import { isProductionHost } from './utils/environment';

/*
 * /blog/who-writes-your-programme — rebuilt 2 Sep 2026 in the article format.
 *
 * NOT YET CLINICALLY REVIEWED. The byline said "reviewed by an
 * HCPC-registered physiotherapist", which asserted a review nobody had
 * done. It now says who wrote it. The reviewer line goes back when RES-21
 * supplies a name and registration number, and the page should not be
 * promoted to production before then.
 *
 * Format from Mockups/Blog_Page_Format.html. Named as the question rather than
 * "clinical governance", because that is the phrasing the objection arrives in
 * and what someone types into a search box.
 *
 * The wedge: we are digital first but human-written. Every competitor in this
 * space leads with AI. We lead with the physiotherapist, and the software is
 * what lets one of them cover a workforce.
 *
 * Claims gate: measures, presents, indicates, supports. Never diagnoses,
 * treats, cures, prescribes, or "clinically proven". No biomarker language.
 */

/* HELD PENDING REVIEW. RES-20 (CMA and CSO on the claims) and RES-21 (a
   named clinician for the byline) are both open, so the article below is
   written but not published. Flip UNDER_REVIEW to false when both land.
   The page and its anchors stay live either way: the footer credit lines
   point here and a dead link is worse than an honest holding page. */
/*
 * Gated by environment, not by a hand-flipped flag.
 *
 * On qa.physioactivity.com the article renders in full so it can be read and
 * reviewed in place. On the live domain it shows the holding page instead,
 * because the claims are not signed off (RES-20) and no named clinician is on
 * the byline yet (RES-21).
 *
 * Nothing needs editing to publish: when the review lands, the gate comes out
 * and the same content goes live on both.
 */
const UNDER_REVIEW = isProductionHost();

const WhoWritesPage = () => (
    UNDER_REVIEW ? <UnderReview title="Who writes your programme" category="How it works" /> : (
    <div className="pa2">
        <div className="art">

            <div className="artwrap">
                <div className="crumb">
                    <Link to="/blog">Blog</Link> <span>&rsaquo;</span> How it works
                </div>

                <h1>Who writes your programme</h1>

                <p className="stand">
                    A registered physiotherapist builds it and owns it. The software gives them the
                    movement data to build from and keeps the loading current, which is what lets
                    one clinician cover a workforce rather than one person at a time.
                </p>

                <div className="byline">
                    <div className="who">
                        <div className="av">PA</div>
                        <div>
                            <div className="nm">PhysioActivity</div>
                            <div className="rl">Written by the PhysioActivity team</div>
                        </div>
                    </div>
                    <div className="bmeta">
                        <span>2 September 2026</span>
                        <i />
                        <span>4 min read</span>
                    </div>
                </div>
            </div>

            <div className="artwrap artbody">
                <p>
                    Almost everything in digital MSK Health leads with AI. We do not, and the
                    distinction is not marketing. Software analyses movement data and proposes
                    adjustments to loading. A named physiotherapist decides what the person
                    actually does, and that decision is recorded against them.
                </p>

                <blockquote>
                    <p>The software proposes. A named clinician decides.</p>
                </blockquote>

                <h2>Who is accountable</h2>
                <p>
                    Every practitioner is registered with the Health and Care Professions Council,
                    the UK statutory regulator, and registration is verified before any access is
                    granted. It is not self-declared.
                </p>
                <p>
                    The person can see who holds their programme, and that clinician&rsquo;s
                    registration number, in the app. If something is wrong, the route is the same
                    as for any UK physiotherapy service: raise it with the practitioner, then with
                    us, then with the HCPC. Being regulated means there is somewhere to go that is
                    not us.
                </p>

                <h2>What the measurement is</h2>
                <p>
                    We read how the body is loaded through movement, not how many steps were taken.
                    Two people can walk the same distance and load completely differently, and that
                    difference is what a programme should respond to.
                </p>

                <div className="contrast">
                    <div className="cc yes">
                        <h3>What we say</h3>
                        <ul>
                            <li>It measures how movement loads the body</li>
                            <li>It indicates change over time</li>
                            <li>It supports a physiotherapist&rsquo;s decision</li>
                            <li>It came out of clinical research</li>
                        </ul>
                    </div>
                    <div className="cc no">
                        <h3>What we do not say</h3>
                        <ul>
                            <li>That it diagnoses anything</li>
                            <li>That it predicts injury</li>
                            <li>That it is clinically proven to change outcomes</li>
                            <li>That it replaces a clinician</li>
                        </ul>
                    </div>
                </div>

                <div className="callout">
                    <b>What this does not claim.</b> The second column is not modesty, it is the
                    evidence position. This is not a medical device, it does not diagnose, and
                    nothing here replaces seeing a clinician when something is wrong.{' '}
                    <Link to="/blog/the-research">Where the measurement came from</Link>.
                </div>

                <h2>Is it a replacement for physiotherapy?</h2>
                <p>
                    No. It is physiotherapy, delivered digitally, by physiotherapists. Where
                    someone needs to be seen in person, they are referred. What changes is that the
                    programme is built from what is actually happening rather than from what
                    someone remembers at their next appointment.
                </p>
            </div>

            <div className="artwrap">
                <h2 className="srch">Sources</h2>
                <ol className="src">
                    <li><a href="https://www.hcpc-uk.org/" target="_blank" rel="noopener noreferrer">Health and Care Professions Council</a>. The UK statutory regulator for physiotherapists, and the route for complaint.</li>
                    <li>University of Southampton. Academic origin of the load measurement method.</li>
                </ol>
                <p className="revnote">Checked 2 September 2026. Reviewed annually.</p>

                <h2 className="srch">Read next</h2>
                <div className="rel">
                    <Link className="rc" to="/blog/the-research">
                        <span className="cat">Evidence</span>
                        <span className="t">The research behind PhysioActivity</span>
                        <span className="x">Where the measurement came from, and the limits of what it supports.</span>
                    </Link>
                    <Link className="rc" to="/trust-centre">
                        <span className="cat">Reference</span>
                        <span className="t">Trust centre</span>
                        <span className="x">What an employer sees, and what they never will.</span>
                    </Link>
                </div>
            </div>

            <section className="artclose">
                <div className="artwrap">
                    <h2>Start where you are.</h2>
                    <p>
                        Your first programme is built from how you actually move this week, not
                        from a questionnaire about how you usually feel.
                    </p>
                    <Link className="btn btn-t" to="/individuals">Get started</Link>
                </div>
            </section>

        </div>
    </div>
)
);

export default WhoWritesPage;
